import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

const ICONS = {
  plus: <path d="M12 5v14M5 12h14" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>),
  refresh: (<><path d="M20 7v5h-5" /><path d="M19 12a7 7 0 1 0-2 5" /></>),
  upload: <path d="M12 16V4m0 0 4 4m-4-4-4 4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />,
  download: <path d="M12 4v12m0 0 4-4m-4 4-4-4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />,
  check: <path d="m5 12 5 5L20 7" />,
  clock: (<><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></>),
  x: <path d="M18 6 6 18M6 6l12 12" />,
  alert: (<><path d="M12 3 2 20h20L12 3z" /><path d="M12 10v4M12 17h.01" /></>),
  half: (<><circle cx="12" cy="12" r="8" /><path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none" /></>),
  gavel: <path d="m14 4 6 6M4 20l8-8M10 8l6 6M12 6l-2 2 6 6 2-2z" />,
  chevron: <path d="m9 18 6-6-6-6" />,
  edit: <path d="M4 20h4l11-11-4-4L4 16zM13.5 6.5l4 4" />,
  trash: (<><path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" /><path d="M10 11v6M14 11v6" /></>),
  file: (<><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v5h5" /></>),
  external: <path d="M14 4h6v6M20 4 10 14M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />,
  dollar: (<><path d="M12 3v18" /><path d="M16 7.5c0-1.7-1.8-3-4-3s-4 1.3-4 3 1.8 3 4 3 4 1.3 4 3-1.8 3-4 3-4-1.3-4-3" /></>),
  shield: (<><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" /><path d="m9 12 2 2 4-4" /></>),
};

export function Icon({ name, size = 16, className }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

export function PageHeader({ title, subtitle, meta, actions }) {
  return (
    <header className="ops-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
        {meta && <div className="ops-header__meta">{meta}</div>}
      </div>
      {actions && <div className="ops-actions">{actions}</div>}
    </header>
  );
}

export function StatCard({ label, value, sub, tone, to }) {
  const cls = `ops-kpi${tone ? ` ops-kpi--${tone}` : ''}`;
  const inner = (
    <>
      <span className="ops-kpi__label">{label}</span>
      <span className="ops-kpi__value">{value}</span>
      {sub && <span className="ops-kpi__sub">{sub}</span>}
    </>
  );
  return to ? <Link to={to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

export function Panel({ title, count, action, children, flush, foot, className = '' }) {
  return (
    <section className={`ops-panel ${className}`}>
      {(title || action) && (
        <div className="ops-panel__head">
          <div className="ops-panel__title">
            {title && <h2>{title}</h2>}
            {count !== undefined && <span className="ops-panel__count">{count}</span>}
          </div>
          {action}
        </div>
      )}
      <div className={`ops-panel__body${flush ? ' ops-panel__body--flush' : ''}`}>{children}</div>
      {foot && <div className="ops-panel__foot">{foot}</div>}
    </section>
  );
}

// One status vocabulary for the whole platform. Each entry has an icon so colour is never the only cue.
const STATUS_TONES = {
  paid: { tone: 'green', label: 'Paid', icon: 'check' },
  pending: { tone: 'amber', label: 'Pending', icon: 'clock' },
  partial_paid: { tone: 'blue', label: 'Partial Paid', icon: 'half' },
  denied: { tone: 'red', label: 'Denied', icon: 'x' },
  appealed: { tone: 'purple', label: 'Appealed', icon: 'gavel' },
  active: { tone: 'green', label: 'Active', icon: 'check' },
  pending_review: { tone: 'amber', label: 'Pending review', icon: 'clock' },
  expired: { tone: 'red', label: 'Expired', icon: 'alert' },
  expiring: { tone: 'amber', label: 'Expiring', icon: 'alert' },
  signed: { tone: 'green', label: 'Signed', icon: 'check' },
  pending_signature: { tone: 'amber', label: 'Pending signature', icon: 'clock' },
  draft: { tone: 'neutral', label: 'Draft', icon: 'edit' },
  'service-ended': { tone: 'neutral', label: 'Service ended', icon: 'x' },
  new: { tone: 'amber', label: 'New', icon: 'alert' },
  contacted: { tone: 'blue', label: 'Contacted', icon: 'clock' },
  converted: { tone: 'green', label: 'Converted', icon: 'check' },
  lost: { tone: 'neutral', label: 'Lost', icon: 'x' },
};

export function StatusPill({ status, label }) {
  const meta = STATUS_TONES[status] || { tone: 'neutral', label: status || 'Unknown', icon: null };
  return (
    <span className={`ops-pill ops-pill--${meta.tone}`}>
      {meta.icon && <Icon name={meta.icon} size={12} />}
      {label || meta.label}
    </span>
  );
}

export function Empty({ title, children }) {
  return (
    <div className="ops-empty">
      {title && <h3>{title}</h3>}
      {children}
    </div>
  );
}

export function Skeleton({ rows = 4 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16 }}>
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="ops-skeleton" style={{ width: `${90 - i * 12}%` }} />)}
    </div>
  );
}

export function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);
  const node = toast ? <div className={`ops-toast ${toast.type}`}>{toast.msg}</div> : null;
  return [show, node];
}

export function SortTh({ col, label, sort, onSort, className = '' }) {
  const active = sort.col === col;
  return (
    <th className={`sortable ${className}${active ? ' is-active' : ''}`} onClick={() => onSort(col)}>
      {label}<span>{active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );
}

export function useSort(defaultCol, defaultDir = 'desc') {
  const [sort, setSort] = useState({ col: defaultCol, dir: defaultDir });
  const onSort = (col) => setSort(s => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  const apply = (rows, getter) => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = getter(a, sort.col); const bv = getter(b, sort.col);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const r = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? r : -r;
    });
    return out;
  };
  return { sort, onSort, apply };
}

export const fmtMoney = (cents) => {
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
export const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${y}`;
};
export const fmtNum = (n, digits = 0) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits });

export const PAGE_SIZES = [10, 25, 50];

// Page numbers to show: first, last, and a window around the current page, with gaps marked by '...'.
function pageItems(totalPages, page) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const items = [1];
  const lo = Math.max(2, page - 1), hi = Math.min(totalPages - 1, page + 1);
  if (lo > 2) items.push('gap-lo');
  for (let p = lo; p <= hi; p++) items.push(p);
  if (hi < totalPages - 1) items.push('gap-hi');
  items.push(totalPages);
  return items;
}

// Table pagination footer. `page` is 1-based; `total` is the row count after filtering.
export function Pager({ page, pageSize, total, onPage, onPageSize, sizes = PAGE_SIZES, label = 'rows' }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(total, current * pageSize);
  return (
    <div className="ops-pager">
      <label className="ops-pager__size">
        Rows per page
        <select className="ops-select ops-select--sm" value={pageSize} onChange={e => onPageSize(Number(e.target.value))} aria-label="Rows per page">
          {sizes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <span className="ops-pager__range">{total === 0 ? `No ${label}` : `Showing ${start} to ${end} of ${total} ${label}`}</span>
      <nav className="ops-pager__nav" aria-label="Pagination">
        <button type="button" className="ops-btn ops-btn--sm" onClick={() => onPage(current - 1)} disabled={current <= 1}>Previous</button>
        {pageItems(totalPages, current).map(item => typeof item === 'number'
          ? <button key={item} type="button" className={`ops-pager__num${item === current ? ' is-active' : ''}`} aria-current={item === current ? 'page' : undefined} onClick={() => onPage(item)}>{item}</button>
          : <span key={item} className="ops-pager__gap">...</span>)}
        <button type="button" className="ops-btn ops-btn--sm" onClick={() => onPage(current + 1)} disabled={current >= totalPages}>Next</button>
      </nav>
    </div>
  );
}
